import React, { useState, useEffect } from 'react';
import { Timeline, Tag, Spin, Empty, message, Tooltip, Button, Alert } from 'antd';
import { 
    TruckOutlined, 
    CheckCircleOutlined, 
    ClockCircleOutlined,
    ReloadOutlined,
    SendOutlined,
    HomeOutlined,
    EnvironmentOutlined,
    LoadingOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import MarketPlaceService from '../../../utils/api/marketplace';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

dayjs.locale('es');

const TrackingTimeline = ({ orderId, shipmentId, trackingNumber }) => {
    const [trackingInfo, setTrackingInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [debugInfo, setDebugInfo] = useState(null);

    const fetchTracking = async () => {
        if (!shipmentId && !trackingNumber) {
            setError('No hay información de rastreo disponible');
            setDebugInfo({ error: 'No shipmentId or trackingNumber provided' });
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const trackingId = shipmentId || trackingNumber;
            
            
            const response = await MarketPlaceService.trackDHLShipment(trackingId);
            
            
            let data = response;
            if (response && response.data) {
                data = response.data;
            }
            
            if (data && data.success === true) {
                if (data.status === 'UNKNOWN' || (data.events && data.events.length === 0)) {
                    setTrackingInfo({
                        ...data,
                        isNew: true,
                        message: 'La guía ha sido generada recientemente. El rastreo estará disponible en las próximas horas.'
                    });
                } else {
                    setTrackingInfo(data);
                }
            } else if (data && data.success === false) {
                if (data.error === "Envío no encontrado") {
                    setError(`El número de guía ${trackingId} aún no está disponible en el sistema de DHL. Esto es normal, puede tardar unos minutos en activarse. Por favor, intenta de nuevo más tarde.`);
                } else {
                    setError(data.message || data.error || 'No se pudo obtener la información de rastreo');
                }
            } else {
                throw new Error('Respuesta inválida del servidor');
            }
        } catch (error) {
            
            if (error.message?.includes('404') || error.response?.status === 404) {
                setError(`La guía aún no está disponible en el sistema de DHL. Por favor, espera unos minutos y vuelve a intentarlo. Si el problema persiste, contacta a atención al cliente.`);
            } else {
                setError(error.response?.data?.message || error.message || 'Error al obtener el rastreo');
            }
            
            setDebugInfo({ 
                error: error.message, 
                stack: error.stack,
                status: error.response?.status 
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (shipmentId || trackingNumber) {
            fetchTracking();
        }
    }, [shipmentId, trackingNumber]);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                <p style={{ marginTop: 16 }}>Obteniendo información de rastreo...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <Alert
                    message="Información de rastreo"
                    description={
                        <div>
                            <p style={{ color: '#faad14' }}>{error}</p>
                            <div style={{ marginTop: 12, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                                <InfoCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                                <span style={{ fontSize: 12, color: '#666' }}>
                                    Si la guía se generó hace menos de 24 horas, es normal que el rastreo aún no esté disponible.
                                </span>
                            </div>
                            {debugInfo && (
                                <details style={{ marginTop: '12px', fontSize: '12px' }}>
                                    <summary>Detalles técnicos</summary>
                                    <pre style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', overflow: 'auto' }}>
                                        {JSON.stringify(debugInfo, null, 2)}
                                    </pre>
                                </details>
                            )}
                        </div>
                    }
                    type="warning"
                    showIcon
                    icon={<InfoCircleOutlined />}
                />
                {(shipmentId || trackingNumber) && (
                    <Button 
                        type="primary" 
                        icon={<ReloadOutlined />}
                        onClick={fetchTracking}
                        style={{ marginTop: 16 }}
                    >
                        Reintentar
                    </Button>
                )}
            </div>
        );
    }

    if (!trackingInfo) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <Empty description="No hay información de rastreo disponible" />
                {debugInfo && (
                    <details style={{ marginTop: '16px' }}>
                        <summary>Ver detalles de la respuesta</summary>
                        <pre style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', overflow: 'auto', fontSize: '12px' }}>
                            {JSON.stringify(debugInfo, null, 2)}
                        </pre>
                    </details>
                )}
            </div>
        );
    }

    if (trackingInfo.isNew || (!trackingInfo.events || trackingInfo.events.length === 0)) {
        return (
            <div className="tracking-timeline-container">
                <div style={{ 
                    background: '#fff7e6', 
                    padding: '16px', 
                    borderRadius: '8px',
                    marginBottom: '16px',
                    borderLeft: '4px solid #faad14'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <InfoCircleOutlined style={{ fontSize: '24px', color: '#faad14' }} />
                        <div style={{ flex: 1 }}>
                            <strong style={{ fontSize: '14px' }}>Guía generada exitosamente</strong>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                                {trackingInfo.message || 'El paquete ha sido registrado en DHL. El rastreo estará disponible en las próximas horas.'}
                            </p>
                            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#999' }}>
                                <ClockCircleOutlined style={{ marginRight: 4 }} />
                                Por lo general, los eventos de seguimiento aparecen entre 2 y 24 horas después de generar la guía.
                            </p>
                        </div>
                        <Tooltip title="Actualizar estado">
                            <Button 
                                size="small" 
                                icon={<ReloadOutlined />} 
                                onClick={fetchTracking}
                                loading={loading}
                            >
                                Actualizar
                            </Button>
                        </Tooltip>
                    </div>
                </div>

                <div style={{ 
                    padding: '24px', 
                    textAlign: 'center',
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    border: '1px dashed #d9d9d9'
                }}>
                    <TruckOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
                    <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 500 }}>Envío registrado en DHL</h4>
                    <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>
                        Número de guía: <strong>{trackingInfo.tracking_number || trackingNumber}</strong>
                    </p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#999' }}>
                        Estado: En espera de actualización del sistema DHL
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="tracking-timeline-container">
            <div style={{ 
                background: '#f0f5ff', 
                padding: '12px 16px', 
                borderRadius: '8px',
                marginBottom: '16px',
                borderLeft: '4px solid #1890ff'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                        <strong style={{ fontSize: '14px' }}>Estado Actual: </strong>
                        <Tag color={trackingInfo.status === 'DELIVERED' ? 'green' : 'blue'} style={{ marginLeft: '8px' }}>
                            {trackingInfo.status_text || trackingInfo.status || 'En tránsito'}
                        </Tag>
                    </div>
                    <div>
                        <Tooltip title="Actualizar estado">
                            <ReloadOutlined 
                                style={{ cursor: 'pointer', color: '#1890ff', fontSize: '16px' }} 
                                onClick={fetchTracking}
                            />
                        </Tooltip>
                    </div>
                </div>
                {trackingInfo.tracking_number && (
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                        <strong>Número de Guía:</strong> {trackingInfo.tracking_number}
                    </p>
                )}
                {trackingInfo.estimated_delivery && (
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                        <ClockCircleOutlined /> Entrega estimada: {dayjs(trackingInfo.estimated_delivery).format('DD/MM/YYYY')}
                    </p>
                )}
            </div>

            <Timeline
                mode="left"
                items={trackingInfo.events.map((event, index) => ({
                    dot: getEventIcon(event),
                    color: getEventColor(event),
                    children: (
                        <div style={{ 
                            padding: '8px 12px',
                            background: index === 0 ? '#f6ffed' : 'transparent',
                            borderRadius: '6px',
                            transition: 'all 0.3s'
                        }}>
                            <div style={{ marginBottom: '4px' }}>
                                <strong>{event.description || event.status || 'Evento'}</strong>
                            </div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                {formatEventDate(event.date, event.time)}
                            </div>
                            {event.location && (
                                <div style={{ fontSize: '11px', color: '#bfbfbf', marginTop: '4px' }}>
                                    <EnvironmentOutlined /> {event.location}
                                </div>
                            )}
                        </div>
                    ),
                }))}
            />
        </div>
    );
};

const getEventIcon = (event) => {
    const description = (event.description || event.status || '').toLowerCase();
    
    if (description.includes('entregado') || description.includes('delivered')) {
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    if (description.includes('transito') || description.includes('en camino') || description.includes('transit')) {
        return <TruckOutlined style={{ color: '#1890ff' }} />;
    }
    if (description.includes('recibido') || description.includes('received')) {
        return <SendOutlined style={{ color: '#fa8c16' }} />;
    }
    if (description.includes('destino') || description.includes('destination')) {
        return <HomeOutlined style={{ color: '#722ed1' }} />;
    }
    return <EnvironmentOutlined style={{ color: '#8c8c8c' }} />;
};

const getEventColor = (event) => {
    const description = (event.description || event.status || '').toLowerCase();
    
    if (description.includes('entregado') || description.includes('delivered')) return 'green';
    if (description.includes('transito') || description.includes('en camino') || description.includes('transit')) return 'blue';
    if (description.includes('recibido') || description.includes('received')) return 'orange';
    if (description.includes('destino') || description.includes('destination')) return 'purple';
    return 'gray';
};

const formatEventDate = (date, time) => {
    if (date && time) {
        return dayjs(`${date} ${time}`).format('DD/MM/YYYY HH:mm');
    }
    if (date) {
        return dayjs(date).format('DD/MM/YYYY HH:mm');
    }
    return 'Fecha no disponible';
};

export default TrackingTimeline;